import type { AxiosRequestConfig } from 'axios'
import { httpClient } from '@/configs/axios.config'
import type {
  CreateOgsmActualInput,
  CreateOgsmNodeInput,
  CreateOgsmRevisionInput,
  OgsmActual,
  OgsmArchiveResult,
  OgsmAssignee,
  OgsmBoard,
  OgsmNode,
  OgsmRemovalImpact,
  OgsmSession,
  OgsmSessionList,
  UpdateOgsmNodeInput,
} from '@/types/api'

// Reads handle a missing board inline instead of redirecting to /not-found.
const readConfig: AxiosRequestConfig = { skipNotFoundRedirect: true }

const root = (companyId: string) => `/companies/${companyId}/ogsm`

export async function listOgsmSessions(companyId: string) {
  const { data } = await httpClient.get<OgsmSessionList>(
    `${root(companyId)}/sessions`,
    readConfig
  )
  return data
}

/** The backend picks the year: the company's current fiscal year. */
export async function createOgsmSession(companyId: string) {
  const { data } = await httpClient.post<OgsmSession>(
    `${root(companyId)}/sessions`,
    {}
  )
  return data
}

export async function getOgsmBoard(companyId: string, sessionId: string) {
  const { data } = await httpClient.get<OgsmBoard>(
    `${root(companyId)}/sessions/${sessionId}`,
    readConfig
  )
  return data
}

/** The backend checks every flagged objective; a refusal names it (400). */
export async function publishOgsmSession(companyId: string, sessionId: string) {
  const { data } = await httpClient.post<OgsmSession>(
    `${root(companyId)}/sessions/${sessionId}/publish`,
    undefined,
    // The dialog shows the backend message inline (ogsmErrorMessage).
    { skipErrorToast: true }
  )
  return data
}

export async function reopenOgsmSession(companyId: string, sessionId: string) {
  const { data } = await httpClient.post<OgsmSession>(
    `${root(companyId)}/sessions/${sessionId}/reopen`,
    undefined,
    { skipErrorToast: true }
  )
  return data
}

export async function updateOgsmStatement(
  companyId: string,
  sessionId: string,
  statement: string | null
) {
  const { data } = await httpClient.patch<OgsmSession>(
    `${root(companyId)}/sessions/${sessionId}`,
    { statement },
    // The statement editor shows the backend message inline (ogsmErrorMessage).
    { skipErrorToast: true }
  )
  return data
}

export async function createOgsmNode(
  companyId: string,
  sessionId: string,
  input: CreateOgsmNodeInput
) {
  const { data } = await httpClient.post<OgsmNode>(
    `${root(companyId)}/sessions/${sessionId}/nodes`,
    input,
    // Every caller shows the backend message inline (ogsmErrorMessage).
    { skipErrorToast: true }
  )
  return data
}

export async function updateOgsmNode(
  companyId: string,
  nodeId: string,
  input: UpdateOgsmNodeInput
) {
  const { data } = await httpClient.patch<OgsmNode>(
    `${root(companyId)}/nodes/${nodeId}`,
    input
  )
  return data
}

export async function deleteOgsmNode(companyId: string, nodeId: string) {
  // DeleteNodeDialog shows every failure inline.
  await httpClient.delete(`${root(companyId)}/nodes/${nodeId}`, {
    skipErrorToast: true,
  })
}

/** What a removal would do: DELETE (no data), ARCHIVE (has data) or BLOCKED (with why). */
export async function getOgsmRemovalImpact(companyId: string, nodeId: string) {
  const { data } = await httpClient.get<OgsmRemovalImpact>(
    `${root(companyId)}/nodes/${nodeId}/removal-impact`,
    // DeleteNodeDialog shows a failed or refused read inline.
    { ...readConfig, skipErrorToast: true }
  )
  return data
}

/** Takes a node with recorded data off the board; its history is kept. */
export async function archiveOgsmNode(companyId: string, nodeId: string) {
  const { data } = await httpClient.post<OgsmArchiveResult>(
    `${root(companyId)}/nodes/${nodeId}/archive`,
    undefined,
    // DeleteNodeDialog shows every failure inline.
    { skipErrorToast: true }
  )
  return data
}

export async function moveOgsmNode(
  companyId: string,
  nodeId: string,
  position: number
) {
  const { data } = await httpClient.post<OgsmNode>(
    `${root(companyId)}/nodes/${nodeId}/move`,
    { position },
    // The board shows the backend message under the moved node (ogsmErrorMessage).
    { skipErrorToast: true }
  )
  return data
}

export async function listOgsmAssignees(companyId: string) {
  const { data } = await httpClient.get<OgsmAssignee[]>(
    `${root(companyId)}/assignees`,
    readConfig
  )
  return data
}

/** Newest first. */
export async function listOgsmActuals(companyId: string, nodeId: string) {
  const { data } = await httpClient.get<OgsmActual[]>(
    `${root(companyId)}/nodes/${nodeId}/actuals`,
    // Both callers show a failed history inline (ActualHistory).
    { ...readConfig, skipErrorToast: true }
  )
  return data
}

export async function addOgsmActual(
  companyId: string,
  nodeId: string,
  input: CreateOgsmActualInput
) {
  const { data } = await httpClient.post<OgsmActual>(
    `${root(companyId)}/nodes/${nodeId}/actuals`,
    input,
    // Every caller shows the backend message inline (ogsmErrorMessage).
    { skipErrorToast: true }
  )
  return data
}

export async function reviseOgsmTarget(
  companyId: string,
  nodeId: string,
  input: CreateOgsmRevisionInput
) {
  const { data } = await httpClient.post<OgsmNode>(
    `${root(companyId)}/nodes/${nodeId}/revisions`,
    input
  )
  return data
}
